import cx from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import { interactiveFloorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { Icon } from '@components/icon/Icon';
import { WideView } from '@components/wideView/WideView';
import {
  getComboAwareActiveListingIds,
  getComboAwareTableIds,
} from '@shared/floorPlans/comboFloorPlanTables';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { useResetZoom } from '@shared/hooks/useResetZoom';
import type { FloorPlanTableData } from '@shared/types/floorPlans';
import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import typography from '~styles/typography.scss';
import { UnavailableTableModal } from '../restaurant/UnavailableTableModal';
import styles from './InteractiveGuestFloorPlan.scss';

export const HAS_SEEN_PULSING_TABLE_GUIDANCE =
  'peak-has-seen-pulsing-table-guidance';

interface InteractiveGuestFloorPlanProps {
  backgroundSrc?: string;
  className?: string;
  onSelectableTableClick?: (args: {
    floorPlanTable: FloorPlanTableData;
    listingIds: string[];
  }) => void;
  tables: FloorPlanTableData[];
  restaurantId: string;
  showUnavailableTableModal?: boolean;
  showZoomControls?: boolean;
  showClearSelectionButton?: boolean;
  disableBackgroundClickClear?: boolean;
}

export const InteractiveGuestFloorPlan = ({
  backgroundSrc,
  className,
  onSelectableTableClick,
  tables,
  restaurantId,
  showUnavailableTableModal = true,
  showZoomControls = true,
  showClearSelectionButton = true,
  disableBackgroundClickClear = false,
}: InteractiveGuestFloorPlanProps) => {
  const hasSeenPulsingTableGuidance =
    localStorage.getItem(HAS_SEEN_PULSING_TABLE_GUIDANCE) === 'true';
  const zoomRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [hoveredFloorPlanTableId, setHoveredFloorPlanTableId] = useState<
    string | null
  >(null);
  const {
    clearSelectedAvailability,
    selectedAvailability,
    selectedFloorPlanListingIds,
    setSelectedFloorPlanListingIds,
    expandedAvailabilityListingIds,
    selectedDate,
    selectedGuestCount,
  } = useAvailabilityContext();

  useResetZoom(zoomRef, [selectedAvailability], !!selectedAvailability);

  const {
    isOpenWithData: {
      isOpen: isUnavailableModalOpen,
      data: unavailableModalData,
    },
    open: openUnavailableModal,
    close: closeUnavailableModal,
  } = useIsOpenWithData<{
    floorPlanTable: FloorPlanTableData;
  }>();

  const highlightedListingIds = useMemo(
    () =>
      selectedAvailability && !selectedAvailability.isBestAvailable
        ? [selectedAvailability.listingId]
        : selectedFloorPlanListingIds,
    [selectedAvailability, selectedFloorPlanListingIds],
  );

  const calculateIsSelectable = useCallback(
    (floorPlanTable: FloorPlanTableData) =>
      floorPlanTable.listings.some((listing) =>
        expandedAvailabilityListingIds.includes(listing.id),
      ),
    [expandedAvailabilityListingIds],
  );

  const calculateIsHighlighted = useCallback(
    (floorPlanTable: FloorPlanTableData): boolean =>
      calculateIsSelectable(floorPlanTable) &&
      floorPlanTable.listings.some((listing) =>
        highlightedListingIds.includes(listing.id),
      ),
    [calculateIsSelectable, highlightedListingIds],
  );

  const handleTableOnClick = useCallback(
    (floorPlanTable: FloorPlanTableData) => {
      if (calculateIsSelectable(floorPlanTable)) {
        const activeListingIds = floorPlanTable.listings
          .filter((listing) =>
            expandedAvailabilityListingIds.includes(listing.id),
          )
          .map(({ id }) => id);
        const listingIds = getComboAwareActiveListingIds(
          tables,
          floorPlanTable,
          activeListingIds,
          selectedGuestCount,
        );

        if (onSelectableTableClick) {
          onSelectableTableClick({ floorPlanTable, listingIds });
        } else {
          clearSelectedAvailability();
          setSelectedFloorPlanListingIds(listingIds);
        }

        if (!hasSeenPulsingTableGuidance) {
          window.localStorage.setItem(HAS_SEEN_PULSING_TABLE_GUIDANCE, 'true');
        }
        return;
      }

      if (showUnavailableTableModal) {
        openUnavailableModal({
          floorPlanTable,
        });
      }
    },
    [
      calculateIsSelectable,
      clearSelectedAvailability,
      expandedAvailabilityListingIds,
      hasSeenPulsingTableGuidance,
      onSelectableTableClick,
      openUnavailableModal,
      selectedGuestCount,
      setSelectedFloorPlanListingIds,
      showUnavailableTableModal,
      tables,
    ],
  );

  const clearFloorPlanSelection = useCallback(() => {
    clearSelectedAvailability();
    setSelectedFloorPlanListingIds([]);
  }, [clearSelectedAvailability, setSelectedFloorPlanListingIds]);

  const handleTableOnMouseEnter = useCallback(
    (floorPlanTable: FloorPlanTableData) => {
      setHoveredFloorPlanTableId(floorPlanTable.id);
    },
    [],
  );

  const handleTableOnMouseLeave = useCallback(() => {
    setHoveredFloorPlanTableId(null);
  }, []);

  const hoveredComboTableIds = useMemo(() => {
    if (!hoveredFloorPlanTableId) {
      return [];
    }

    const hoveredTable = tables.find(
      (table) => table.id === hoveredFloorPlanTableId,
    );

    if (!hoveredTable || !calculateIsSelectable(hoveredTable)) {
      return [];
    }

    const activeListingIds = hoveredTable.listings
      .filter((listing) => expandedAvailabilityListingIds.includes(listing.id))
      .map(({ id }) => id);

    return getComboAwareTableIds(
      tables,
      hoveredTable,
      activeListingIds,
      selectedGuestCount,
    );
  }, [
    calculateIsSelectable,
    expandedAvailabilityListingIds,
    hoveredFloorPlanTableId,
    selectedGuestCount,
    tables,
  ]);

  const calculateIsComboHovered = useCallback(
    (floorPlanTable: FloorPlanTableData): boolean =>
      hoveredComboTableIds.includes(floorPlanTable.id),
    [hoveredComboTableIds],
  );

  const tablesWithPulse = useMemo(() => {
    const firstSelectableTable = tables.find((table) =>
      calculateIsSelectable(table),
    );

    if (!firstSelectableTable || hasSeenPulsingTableGuidance) {
      return tables;
    }

    return tables.map((table) => {
      if (table.id === firstSelectableTable.id) {
        return { ...table, pulse: true };
      }

      return table;
    });
  }, [calculateIsSelectable, hasSeenPulsingTableGuidance, tables]);

  const floorPlanTablesRenderer: FloorPlanTablesRenderer = useMemo(
    () =>
      interactiveFloorPlanTablesRendererFactory<FloorPlanTableData>({
        calculateIsComboHovered,
        calculateIsHighlighted,
        calculateIsSelectable,
        handleTableOnClick,
        handleTableOnMouseEnter,
        handleTableOnMouseLeave,
        tables: tablesWithPulse,
      }),
    [
      calculateIsComboHovered,
      calculateIsHighlighted,
      calculateIsSelectable,
      handleTableOnClick,
      handleTableOnMouseEnter,
      handleTableOnMouseLeave,
      tablesWithPulse,
    ],
  );

  return (
    <div className={styles.floorPlanContainer}>
      <FloorPlan
        backgroundSrc={backgroundSrc}
        className={className}
        floorPlanTablesRenderer={floorPlanTablesRenderer}
        handleBackgroundOnClick={
          disableBackgroundClickClear ? undefined : clearFloorPlanSelection
        }
        ref={zoomRef}
        showZoomControls={showZoomControls}
      />
      {showClearSelectionButton && !!selectedFloorPlanListingIds.length && (
        <WideView
          narrowElement={
            <button
              aria-label="Clear floor plan selection"
              className={styles.clearSelectionButton}
              data-testid="clear-floor-plan-selection-button-narrow"
              onClick={clearFloorPlanSelection}
            >
              <Icon className={styles.resetIcon} name="reset" />
            </button>
          }
          wideElement={
            <button
              className={cx(typography.t1, styles.clearSelectionButton)}
              data-testid="clear-floor-plan-selection-button-wide"
              onClick={clearFloorPlanSelection}
            >
              <Icon className={styles.resetIcon} name="reset" /> Clear floor
              plan selection
            </button>
          }
        />
      )}
      {isUnavailableModalOpen && showUnavailableTableModal && (
        <UnavailableTableModal
          isOpen
          onClose={closeUnavailableModal}
          selectedDate={selectedDate}
          restaurantId={restaurantId}
          floorPlanTable={unavailableModalData.floorPlanTable}
        />
      )}
    </div>
  );
};
